import React, { useState } from "react";
import { useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import { useGetNcsPerformanceQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  labelClass,
  btnGhost,
  fmtDateInput,
} from "./config";
import { downloadPerformanceExcel } from "../../../../services/netplusCustomerStatus";
import { uiToast } from "./ui";

const COLS = [
  { k: "rechargesMarked", label: "Recharges marked" },
  { k: "rechargesConfirmed", label: "Confirmed" },
  { k: "modemTransfers", label: "Modem transfers" },
  { k: "modemRecovered", label: "Modem recovered" },
  { k: "modemNotRecovered", label: "Not recovered" },
  { k: "newConnections", label: "New connections" },
];

const monthStart = () => {
  const d = new Date();
  return fmtDateInput(new Date(d.getFullYear(), d.getMonth(), 1));
};

const NcsPerformance = () => {
  const role = useSelector(selectUserRole);
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(fmtDateInput());

  const { data, isFetching } = useGetNcsPerformanceQuery(
    { partnerCode, from, to },
    { skip: role !== "super-admin" },
  );
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Performance" motherMenu="Netplus" />
        <div className={`${panelClass} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
        </div>
      </>
    );
  }

  const chart = {
    options: {
      chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
      plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
      colors: ["#16a34a", "#0891b2", "#d97706"],
      dataLabels: { enabled: false },
      grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
      xaxis: {
        categories: rows.map((r) => r.name),
        labels: { style: { fontSize: "11px", colors: "#94a3b8" }, rotate: -30 },
      },
      yaxis: { labels: { style: { fontSize: "11px", colors: "#94a3b8" } } },
      legend: { position: "top", fontSize: "12px" },
    },
    series: [
      { name: "Recharges marked", data: rows.map((r) => r.rechargesMarked) },
      { name: "Modem recovered", data: rows.map((r) => r.modemRecovered) },
      { name: "New connections", data: rows.map((r) => r.newConnections) },
    ],
  };

  return (
    <>
      <PageTitle activeMenu="Performance" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <div>
            <span className={labelClass}>From</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <span className={labelClass}>To</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
            <button
              className={btnGhost}
              onClick={() =>
                downloadPerformanceExcel(partnerCode, { from, to }).catch((e) =>
                  uiToast({
                    type: "error",
                    title: "Export failed",
                    message: e?.response?.data?.message,
                  }),
                )
              }
            >
              Download Excel
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Employee</th>
                {COLS.map((c) => (
                  <th key={c.k} className="px-4 py-3 text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={COLS.length + 1} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length + 1} className="px-4 py-12 text-center text-gray-400">
                    No team members / no activity in range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.userId} className="hover:bg-cyan-50/30">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-800">{r.name}</div>
                      <div className="text-xs text-gray-400">{r.role}</div>
                    </td>
                    {COLS.map((c) => (
                      <td key={c.k} className="px-4 py-2.5 text-center tabular-nums">
                        {r[c.k] ?? 0}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500">
                    Total
                  </td>
                  {COLS.map((c) => (
                    <td key={c.k} className="px-4 py-3 text-center tabular-nums">
                      {totals[c.k] || 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {rows.length > 0 && (
          <div className="p-5">
            <ReactApexChart
              options={chart.options}
              series={chart.series}
              type="bar"
              height={300}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default NcsPerformance;
